import parseNumber from "@starter/Framework/Common/Utilities/parseNumber";

export type Amount = number | string;

export const basePixelAmount = 16;

const parseAmount = (amount: Amount) => (typeof amount === "string" ? (parseNumber(amount) ?? 0) : amount);

const toUnit = (fn: (value: number) => string) => {
    return (...amounts: Amount[]) => amounts.map(x => fn(parseAmount(x))).join(" ");
};

const toUnitArray = (fn: ReturnType<typeof toUnit>) => (amount: Array<Amount | null>) => {
    return amount.map(x => (x !== null ? fn(x) : null));
};

// --- PX

export const pxToRem = toUnit(x => `${x / basePixelAmount}rem`);
export const pxToRemArray = toUnitArray(pxToRem);

// --- REM

export const remToPx = toUnit(x => `${x * basePixelAmount}px`);
export const remToPxArray = toUnitArray(remToPx);

// --- IN

export const inToRem = toUnit(x => `${(x * 96) / basePixelAmount}rem`);
export const inToRemArray = toUnitArray(inToRem);

// --- PT

export const ptToRem = toUnit(x => `${(x * 1.3333343412075) / basePixelAmount}rem`);
export const ptToRemArray = toUnitArray(ptToRem);

// --- PC

export const pcToRem = toUnit(x => `${(x * 16) / basePixelAmount}rem`);
export const pcToRemArray = toUnitArray(pcToRem);

// --- CM

export const cmToRem = toUnit(x => `${(x * 37.7952755906) / basePixelAmount}rem`);
export const cmToRemArray = toUnitArray(cmToRem);

// --- MM

export const mmToRem = toUnit(x => `${(x * 37.7952755906) / 10 / basePixelAmount}rem`);
export const mmToRemArray = toUnitArray(mmToRem);

// --- MAPPER

export const unitToRem = (...amounts: Amount[]) => {
    return amounts
        .map(x => {
            if (typeof x === "number") {
                return `${x}rem`;
            }

            const unit = x.match(/[\d]+(?:\.[\d]+)?([a-z]+)/i)?.[1].toLowerCase();

            switch (unit) {
                case "px":
                    return pxToRem(x);
                case "in":
                    return inToRem(x);
                case "pt":
                    return ptToRem(x);
                case "pc":
                    return pcToRem(x);
                case "cm":
                    return cmToRem(x);
                case "mm":
                    return mmToRem(x);
                default:
                    return x;
            }
        })
        .join(" ");
};

export const unitToRemArray = toUnitArray(unitToRem);

// --- HELPERS

export const getUnitsRegex = (units?: string | string[], isOptional?: boolean) => {
    const _units = typeof units === "string" ? [units] : units;

    return new RegExp(
        `^[\\d]+(?:\\.[\\d]+)?${_units && _units.length ? `(${_units.join("|")})${isOptional ? "?" : ""}` : ""}$`,
        "i",
    );
};

export const isUnit = (value: string | undefined | null, units?: string | string[], isOptional?: boolean) => {
    if (!value) {
        return false;
    }

    return getUnitsRegex(units, isOptional).test(value);
};
